import type { OrganizationPlaybookModelEntry } from "../../hooks/useOrganizationPlaybookModels";
import { PROJECT_LIFECYCLE_PHASES } from "../../enums";
import type { ProjectLifecyclePhase } from "../../schema";
import { formatPhase } from "../../selectors/formatters";
import { findDriverTitle } from "../../selectors/lookups";
import type { KnowledgeBaseTextContext } from "../../selectors/shared";

export type OrganizationPlaybookFilterValue = "all" | ProjectLifecyclePhase;

export type OrganizationPlaybookRow = {
  id: string;
  phase: ProjectLifecyclePhase;
  phaseLabel: string;
  actionText: string;
  ownerRole: string;
  sourceProjectName: string;
  sourceProjectHref: string;
  linkedDriverTitles: string[];
  evidenceLabels: string[];
};

export type OrganizationPlaybookOverviewData = {
  title: string;
  subtitle: string;
  totalCount: number;
  emptyTitle: string;
  emptyDescription: string;
  errorTitle: string;
  errorDescription: string;
  openSourceProjectLabel: string;
  groupedRows: [ProjectLifecyclePhase, OrganizationPlaybookRow[]][];
};

const phaseOrder = new Map(PROJECT_LIFECYCLE_PHASES.map((phase, index) => [phase, index] as const));

const createBaseCopy = (
  text: KnowledgeBaseTextContext,
  totalCount: number,
): Omit<OrganizationPlaybookOverviewData, "groupedRows"> => {
  const { t } = text;

  return {
    title: t("knowledgeBase.landing.playbookOverview.title"),
    subtitle: t("knowledgeBase.landing.playbookOverview.subtitle"),
    totalCount,
    emptyTitle: t("knowledgeBase.landing.playbookOverview.empty.title"),
    emptyDescription: t("knowledgeBase.landing.playbookOverview.empty.description"),
    errorTitle: t("knowledgeBase.landing.playbookOverview.error.title"),
    errorDescription: t("knowledgeBase.landing.playbookOverview.error.description"),
    openSourceProjectLabel: t("knowledgeBase.landing.playbookOverview.openSourceProject"),
  };
};

export const selectOrganizationPlaybookOverviewData = (
  entries: OrganizationPlaybookModelEntry[],
  text: KnowledgeBaseTextContext,
): OrganizationPlaybookOverviewData => {
  const rowsByPhase = new Map<ProjectLifecyclePhase, OrganizationPlaybookRow[]>(
    PROJECT_LIFECYCLE_PHASES.map((phase) => [phase, []]),
  );

  for (const { project, model } of entries) {
    const sortedGroups = [...model.playbook.groups].sort((left, right) => {
      const leftIndex = phaseOrder.get(left.phase) ?? Number.MAX_SAFE_INTEGER;
      const rightIndex = phaseOrder.get(right.phase) ?? Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex;
    });

    for (const group of sortedGroups) {
      const currentRows = rowsByPhase.get(group.phase) ?? [];

      currentRows.push(
        ...group.items.map((item) => ({
          id: `${project.id}:${item.id}`,
          phase: group.phase,
          phaseLabel: formatPhase(group.phase, text),
          actionText: item.actionText,
          ownerRole: item.ownerRole,
          sourceProjectName: project.name,
          sourceProjectHref: `/${model.project.orgId}/knowledge-base/${project.id}`,
          linkedDriverTitles: item.linkedDriverIds.map((driverId) =>
            findDriverTitle(model, driverId, text),
          ),
          evidenceLabels: item.evidence.map((evidence) => evidence.title),
        })),
      );

      rowsByPhase.set(group.phase, currentRows);
    }
  }

  const groupedRows = PROJECT_LIFECYCLE_PHASES.map(
    (phase) =>
      [phase, rowsByPhase.get(phase) ?? []] as [ProjectLifecyclePhase, OrganizationPlaybookRow[]],
  ).filter(([_, rows]) => rows.length > 0);

  const totalCount = groupedRows.reduce((acc, [_, rows]) => acc + rows.length, 0);

  return {
    ...createBaseCopy(text, totalCount),
    groupedRows,
  };
};
